import React, { useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';
import { useNeo4j } from '../hooks/useNeo4j';
import RadarChart from './RadarChart';
import ProfileDetails from './ProfileDetails';

function Dashboard() {
  const { state, dispatch } = useApp();
  const { fetchUserProfiles, isRefreshing } = useNeo4j();

  const handleUserChange = (e) => {
    dispatch({ type: ActionTypes.SET_SELECTED_USER, payload: e.target.value });
  };

  const handleRefresh = async () => {
    const currentSelection = state.selectedUser;
    try {
      const newProfiles = await fetchUserProfiles();
      if (newProfiles[currentSelection]) {
        dispatch({ type: ActionTypes.SET_SELECTED_USER, payload: currentSelection });
      } else {
        const firstUser = Object.keys(newProfiles)[0];
        if (firstUser) {
          dispatch({ type: ActionTypes.SET_SELECTED_USER, payload: firstUser });
        }
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
      alert(`Failed to refresh data: ${error.message}`);
    }
  };

  // Set initial selected user when profiles are loaded
  useEffect(() => {
    if (!state.selectedUser && Object.keys(state.userProfiles).length > 0) {
      const firstUser = Object.keys(state.userProfiles)[0];
      dispatch({ type: ActionTypes.SET_SELECTED_USER, payload: firstUser });
    }
  }, [state.userProfiles, state.selectedUser, dispatch]);

  const userNames = Object.keys(state.userProfiles).sort();

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 md:p-8">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="text-left">
          <h1 className="text-3xl font-bold text-white">Personal Work Graph</h1>
          <p className="text-gray-400 mt-2">Live visualization of individual work styles from Neo4j.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'query' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Brainstorming Agent
          </button>
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'queryV2' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Brainstorming Agent v2
          </button>
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'company' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Company Agent
          </button>
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'editor' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Mission Editor Agent
          </button>
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'visualization' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Visualization
          </button>
          <button 
            onClick={() => dispatch({ type: ActionTypes.SET_SCREEN, payload: 'addUser' })}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg border border-gray-600"
          >
            Add New User
          </button>
        </div>
      </div>
      
      <div className="mb-6 flex flex-col sm:flex-row items-center justify-center gap-4">
        <label htmlFor="userSelector" className="font-medium text-lg">
          Select Team Member:
        </label>
        <div className="flex items-center gap-2">
          <select 
            id="userSelector"
            value={state.selectedUser || ''}
            onChange={handleUserChange}
            className="bg-gray-700 border border-gray-600 rounded-lg w-full sm:w-64 p-2.5"
          >
            <option value="">Select a user...</option>
            {userNames.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50"
            title="Refresh user list from database"
          >
            {isRefreshing ? (
              <div className="spinner w-5 h-5"></div>
            ) : (
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.664 0l3.18-3.185m-11.665 0l3.181-3.182m0 0a8.25 8.25 0 00-11.664 0l-3.18 3.185" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div className="w-full">
          <RadarChart />
        </div>
        
        <div className="w-full">
          <ProfileDetails />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
